import type {
  JourneyResponse,
  SectionResponse,
  StoryResponse,
  SubJourneyResponse,
  UploadUrlResponse,
} from '@manasil/shared';
import {
  COVER_IMAGE,
  HOME_HERO,
  IMAGE_CONTENT_TYPES,
  MAX_IMAGE_BYTES,
  STORY_BANNER,
} from '@manasil/shared';
import { api, resolveMediaUrl } from './api.js';
import { put } from './upload.js';

/**
 * Journey covers and story banners, one flow.
 *
 * They differ in shape and in where they land. They do not differ in how a file is checked
 * or how the bytes get there — the API signs, this browser PUTs, the API verifies. Mirrors
 * createImageAssetService on the server side.
 */

export const IMAGE_ACCEPT = IMAGE_CONTENT_TYPES.join(',');

/** The shape of COVER_IMAGE and STORY_BANNER, so one checker serves both. */
export interface ImageSpec {
  width: number;
  height: number;
  aspect: number;
  aspectTolerance: number;
  minWidth: number;
  obscuredFromTop: number;
  /** Only the Home hero has one: its scrim runs left-to-right, not top-down. */
  obscuredFromLeft?: number;
}

export const COVER_SPEC: ImageSpec = COVER_IMAGE;
export const BANNER_SPEC: ImageSpec = STORY_BANNER;
export const HERO_SPEC: ImageSpec = HOME_HERO;

/** "square" reads better than "1:1" to anyone who is not a developer. */
function shapeName(spec: ImageSpec): string {
  return spec.aspect === 1 ? 'square' : `${spec.aspect.toFixed(2)}:1`;
}

const BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/** Browsers disagree about image MIME types far less than audio, but not never. */
function contentTypeOf(file: File): string | null {
  const reported = file.type.toLowerCase();
  if ((IMAGE_CONTENT_TYPES as readonly string[]).includes(reported)) return reported;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[ext] ?? null;
}

export interface ImageCheck {
  width: number;
  height: number;
  aspect: number;
  /** Nothing here means it is exactly right. */
  problems: string[];
  /** Worth saying, but not worth stopping for. */
  warnings: string[];
  previewUrl: string;
}

/**
 * Measures the file in the browser BEFORE anything is uploaded.
 *
 * The API never opens the image, so this is the only place its dimensions can be known —
 * and the only place an editor can be told they have the wrong crop while they still have
 * the right file to hand.
 */
export function inspectImage(file: File, spec: ImageSpec): Promise<ImageCheck> {
  return new Promise((resolve, reject) => {
    const contentType = contentTypeOf(file);
    if (!contentType) {
      reject(new Error(`${file.name} is not a JPEG, PNG or WebP.`));
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = image;
      const aspect = width / height;
      const problems: string[] = [];
      const warnings: string[] = [];

      if (file.size > MAX_IMAGE_BYTES) {
        problems.push(`${(file.size / 1_048_576).toFixed(1)} MB is over the 5 MB limit.`);
      }
      if (width < spec.minWidth) {
        problems.push(
          `${width} px wide — it will look soft. Use at least ${spec.minWidth} px, ideally ${spec.width}.`,
        );
      }
      if (Math.abs(aspect - spec.aspect) > spec.aspectTolerance) {
        const shape = aspect > spec.aspect ? 'wider' : 'taller';
        problems.push(
          `This is ${aspect.toFixed(2)}:1 — ${shape} than the ${shapeName(spec)} the app uses, so it will be cropped. ` +
            `${spec.width} × ${spec.height} fits exactly.`,
        );
      }
      // Worth flagging separately: the right shape but bigger than anyone needs.
      if (problems.length === 0 && file.size > 600_000) {
        warnings.push(
          `${Math.round(file.size / 1024)} KB. It will work, but 150-300 KB is plenty — a smaller file loads faster on mobile data.`,
        );
      }

      resolve({ width, height, aspect, problems, warnings, previewUrl });
    };
    image.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      reject(new Error(`${file.name} could not be read as an image.`));
    };
    image.src = previewUrl;
  });
}

/** Sign, PUT straight to storage, then confirm — the same three steps as the audio. */
async function uploadImage<T>(
  paths: { sign: string; finalize: string },
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<T> {
  const contentType = contentTypeOf(file);
  if (!contentType) throw new Error(`${file.name} is not a JPEG, PNG or WebP.`);

  const signed = await api<UploadUrlResponse>(paths.sign, {
    method: 'POST',
    body: { contentType, sizeBytes: file.size },
  });

  await put(resolveMediaUrl(signed.uploadUrl), file, signed.headers, onProgress);

  return api<T>(paths.finalize, {
    method: 'POST',
    body: { width: check.width, height: check.height },
  });
}

export function uploadJourneyCover(
  journeyId: string,
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<JourneyResponse> {
  return uploadImage<JourneyResponse>(
    {
      sign: `/admin/journeys/${journeyId}/cover-upload-url`,
      finalize: `/admin/journeys/${journeyId}/cover/finalize`,
    },
    file,
    check,
    onProgress,
  );
}

export function removeJourneyCover(journeyId: string): Promise<JourneyResponse> {
  return api<JourneyResponse>(`/admin/journeys/${journeyId}/cover`, { method: 'DELETE' });
}

export function uploadJourneyHero(
  journeyId: string,
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<JourneyResponse> {
  return uploadImage<JourneyResponse>(
    {
      sign: `/admin/journeys/${journeyId}/hero-upload-url`,
      finalize: `/admin/journeys/${journeyId}/hero/finalize`,
    },
    file,
    check,
    onProgress,
  );
}

export function removeJourneyHero(journeyId: string): Promise<JourneyResponse> {
  return api<JourneyResponse>(`/admin/journeys/${journeyId}/hero`, { method: 'DELETE' });
}

export function uploadStoryBanner(
  storyId: string,
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<StoryResponse> {
  return uploadImage<StoryResponse>(
    {
      sign: `/admin/stories/${storyId}/banner-upload-url`,
      finalize: `/admin/stories/${storyId}/banner/finalize`,
    },
    file,
    check,
    onProgress,
  );
}

export function removeStoryBanner(storyId: string): Promise<StoryResponse> {
  return api<StoryResponse>(`/admin/stories/${storyId}/banner`, { method: 'DELETE' });
}

/** A section's image: square, shown on the collection dashboard in place of its icon. */
export function uploadSectionImage(
  sectionId: string,
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<SectionResponse> {
  return uploadImage<SectionResponse>(
    {
      sign: `/admin/sections/${sectionId}/image-upload-url`,
      finalize: `/admin/sections/${sectionId}/image/finalize`,
    },
    file,
    check,
    onProgress,
  );
}

export function removeSectionImage(sectionId: string): Promise<SectionResponse> {
  return api<SectionResponse>(`/admin/sections/${sectionId}/image`, { method: 'DELETE' });
}

/** An adhikaram's banner: the same square spec as a story's, shared by its ten kurals. */
export function uploadSubJourneyBanner(
  subJourneyId: string,
  file: File,
  check: ImageCheck,
  onProgress: (fraction: number) => void,
): Promise<SubJourneyResponse> {
  return uploadImage<SubJourneyResponse>(
    {
      sign: `/admin/sub-journeys/${subJourneyId}/banner-upload-url`,
      finalize: `/admin/sub-journeys/${subJourneyId}/banner/finalize`,
    },
    file,
    check,
    onProgress,
  );
}

export function removeSubJourneyBanner(subJourneyId: string): Promise<SubJourneyResponse> {
  return api<SubJourneyResponse>(`/admin/sub-journeys/${subJourneyId}/banner`, {
    method: 'DELETE',
  });
}
